/* =========================================================================
   telemetry-menu-helpers.ts — reusable menu builder utilities for telemetry
   components (ConfigDrawer, GridDashboard, etc.).
   ========================================================================= */

import React from 'react';
import { MenuItem, TelemetryPanel, UnitSystem } from '../lib/dashboard-types';
import { CAT, unitOf, dotIcon, mkVizIcon, hydrateCatalogFromCache, isNoneUnit } from '../../../../../CommonComponents/telemetry';
import {
  vizLabel,
  subColor,
  scalarChannels,
  makeColumns,
  colKey,
  getChannelDef,
  getSubsystemLabel,
  normalizePanelParameter,
} from '../lib/telemetry-helpers';

interface MenuOpenOptions {
  title?: string;
  width?: number;
  atPointer?: boolean;
  keepPosition?: boolean;
  onBack?: () => void;
  anchorX?: number;
  anchorY?: number;
  alignRight?: boolean;
}

type OpenMenuFn = (e: React.MouseEvent, items: MenuItem[], opts?: MenuOpenOptions) => void;

/**
 * Checks if a parameter definition allows the given graph mode.
 */
export const isParameterCompatibleWithGraph = (d: any, graphMode?: string): boolean => {
  if (!graphMode) return true;
  const allowed: string[] = Array.isArray(d?.allowed) && d.allowed.length > 0
    ? d.allowed
    : ['line', 'area', 'kpi', 'gauge', 'table', 'ctable', 'cchart'];
  if (allowed.includes(graphMode)) return true;
  if (graphMode === 'cchart' && (allowed.includes('cchart') || allowed.includes('line') || allowed.includes('area'))) return true;
  if ((graphMode === 'line' || graphMode === 'area') && (allowed.includes('line') || allowed.includes('area') || allowed.includes('cchart'))) return true;
  return false;
};

/**
 * Builds menu items for picking a subsystem for a widget.
 */
export const buildSubsystemMenu = (
  activeSub: string,
  wId: string,
  patchWidget: (id: string, patch: Partial<TelemetryPanel>) => void,
  closeMenu: () => void
): MenuItem[] => {
  if (Object.keys(CAT).length === 0) {
    hydrateCatalogFromCache();
  }
  return Object.keys(CAT).map((k) => ({
    label: CAT[k]?.label || k,
    checked: activeSub === k,
    onClick: () => {
      const chs = CAT[k]?.params || {};
      const fc = Object.keys(chs)[0] || '';
      const fd = chs[fc] || { label: fc || k, allowed: ['line', 'area', 'kpi'] };
      patchWidget(wId, {
        name: fd.label,
        graphMode: fd.allowed[0],
        parameters: makeColumns(k, fc),
      });
      closeMenu();
    },
  }));
};

/**
 * Builds menu items for picking a telemetry channel for a subsystem.
 */
export const buildTelemetryChannelMenu = (
  activeSub: string,
  activeParamId: string,
  wId: string,
  patchWidget: (id: string, patch: Partial<TelemetryPanel>) => void,
  closeMenu: () => void
): MenuItem[] => {
  if (Object.keys(CAT).length === 0) {
    hydrateCatalogFromCache();
  }
  const chs = CAT[activeSub]?.params;
  if (!chs) return [];

  return Object.keys(chs).map((k) => {
    const d = chs[k] || { label: k, allowed: ['line', 'area', 'kpi'] };
    return {
      label: d.label,
      checked: activeParamId === k,
      onClick: () => {
        patchWidget(wId, {
          name: d.label,
          parameters: makeColumns(activeSub, k),
        });
        closeMenu();
      },
    };
  });
};

/**
 * Builds menu items for switching visualization modes for a widget.
 */
export const buildVisualizationMenu = (
  sub: string,
  paramId: string,
  currentMode: string,
  wId: string,
  patchWidget: (id: string, patch: Partial<TelemetryPanel>) => void,
  closeMenu: () => void,
  firstParam?: any
): MenuItem[] => {
  const def = (firstParam && Array.isArray(firstParam.allowed) && firstParam.allowed.length > 0)
    ? firstParam
    : getChannelDef(sub, paramId);

  const rawAllowed: string[] = Array.isArray(def?.allowed) && def.allowed.length > 0
    ? def.allowed
    : ['line', 'area', 'kpi', 'gauge', 'table', 'ctable', 'cchart', 'pie', 'bar', 'hbar', 'radar'];

  const allowedList = [...rawAllowed];
  if (currentMode && !allowedList.includes(currentMode)) {
    allowedList.push(currentMode);
  }

  const allowed = currentMode === 'kpi'
    ? allowedList.filter((v) => v === 'kpi')
    : allowedList.filter((v) => v !== 'kpi');

  return allowed.map((v) => ({
    label: vizLabel(v),
    icon: mkVizIcon(v),
    checked: currentMode === v,
    onClick: () => {
      patchWidget(wId, { graphMode: v });
      closeMenu();
    },
  }));
};

/**
 * Builds 2-level cascading menu items for adding columns/parameters to a multi-series widget.
 * Filters parameters to only display those compatible with the target graph mode.
 */
export const buildAddParameterMenu = (
  cols: any[],
  wId: string,
  chartUnits: string[],
  maxUnits: number,
  units: UnitSystem,
  patchWidget: (id: string, patch: Partial<TelemetryPanel>) => void,
  openMenu: OpenMenuFn,
  closeMenu: () => void,
  anchorPos?: { x: number; y: number },
  graphMode?: string
): MenuItem[] => {
  if (Object.keys(CAT).length === 0) {
    hydrateCatalogFromCache();
  }
  return Object.keys(CAT)
    .filter((sub) => {
      const channels = scalarChannels(sub);
      return channels.some((k) => isParameterCompatibleWithGraph(getChannelDef(sub, k), graphMode));
    })
    .map((sub) => ({
      label: getSubsystemLabel(sub),
      icon: dotIcon(subColor(sub)),
      onClick: (ev?: React.MouseEvent) => {
        const channels = scalarChannels(sub).filter((k) =>
          isParameterCompatibleWithGraph(getChannelDef(sub, k), graphMode)
        );
        const paramItems: MenuItem[] = channels.map((k) => {
          const d = getChannelDef(sub, k);
          const have = new Set(cols.map((c) => (c.subsystem || c.sub || '') + '.' + (c.paramId || c.paramName || c.tel || '')));
          const added = have.has(sub + '.' + k);
          const u = unitOf(d, units);
          const blocked = !added && maxUnits > 0 && chartUnits.length >= maxUnits && chartUnits.indexOf(u) < 0;
          const displayUnit = !isNoneUnit(d.unit) ? ` (${d.unit})` : '';

          return {
            label: `${d.label}${displayUnit}`,
            checked: added,
            disabled: blocked,
            onClick: () => {
              if (added || blocked) {
                closeMenu();
                return;
              }
              const cur = cols.slice();
              const keys = new Set(cur.map((c) => colKey(c)));
              makeColumns(sub, k).forEach((c) => {
                if (!keys.has(colKey(c))) cur.push(normalizePanelParameter(c));
              });
              patchWidget(wId, { parameters: cur });
              closeMenu();
            },
          };
        });

        openMenu(
          ev as any,
          paramItems,
          {
            title: CAT[sub]?.label || sub,
            width: 240,
            anchorX: anchorPos?.x,
            anchorY: anchorPos?.y,
            onBack: () => {
              const rootItems = buildAddParameterMenu(cols, wId, chartUnits, maxUnits, units, patchWidget, openMenu, closeMenu, anchorPos, graphMode);
              openMenu(ev as any, rootItems, {
                title: 'Add Parameter',
                width: 220,
                anchorX: anchorPos?.x,
                anchorY: anchorPos?.y,
              });
            },
          }
        );
      },
    }));
};
